/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: API Architecture
 * PURPOSE: Core HTTP client with centralized authentication, error handling, and token refresh
 *
 * NEW PATTERNS IMPLEMENTED:
 * - ApiClient Pattern: Centralized HTTP handler with generic types
 * - Error Handling: Comprehensive ApiError parsing with field-level errors
 * - Token Refresh: Automatic retry on 401 with exponential backoff
 * - Safe Variants: Tuple return [data | null, error | null]
 * - Path Builder: Functional endpoint construction via API_CONFIG
 *
 * KEY FEATURES:
 * - Automatic token refresh on 401 with session cleanup
 * - Smart authentication strategy (JWT vs API key)
 * - Safe request methods returning [data, error] tuples
 * - Generic type support for all HTTP methods
 * - Credential management with localStorage sync
 *
 * RELATED FILES:
 * - src/lib/services/api/pathBuilder.ts
 * - src/lib/config/apiConfig.ts
 * - src/lib/types/api/error.types.ts
 * - src/lib/services/auth/TokenService.ts
 */

// src/services/api/client.ts

import { browser } from '$app/environment';
import { healthState, getSnapshot } from '$lib/state/health.state.svelte';
import { API_CONFIG } from '$lib/config/apiConfig';
import { apiPath } from '$lib/services/api/pathBuilder';
import { determineAuthStrategy, getApiKey, getAuthHeaders } from '$lib/services/utils/authUtils';
import type { ApiRequestOptions } from '$lib/types/api/requests.types';
import { ErrorUtils } from '$lib/types/api/error.types';
import { tokenService } from '$lib/services/auth/TokenService';
import { createLogger } from '$lib/utils/logger';
import { secureStorage } from '$lib/utils/secure-storage';

const logger = createLogger('api.client');

/**
 * Base API client for making HTTP requests to the backend
 */
export class ApiClient {
	/**
	 * Base URL for API requests
	 */
	private baseUrl: string;

	/**
	 * In-flight GET request deduplication map.
	 * Key: full URL (including query params), Value: the pending Promise.
	 * Only GET requests are tracked — mutating methods (POST/PUT/PATCH/DELETE) are never deduped.
	 */
	private inflightGets = new Map<string, Promise<unknown>>();

	/**
	 * Active AbortControllers for all in-flight requests.
	 * Used by cancelPending() to abort requests on navigation.
	 */
	private activeControllers = new Set<AbortController>();

	/**
	 * Whether a token refresh is currently in progress (used to queue concurrent 401s).
	 */
	private isRefreshing = false;

	/**
	 * Callbacks queued by concurrent requests that hit 401 while a refresh is already in-flight.
	 * Each callback is invoked with true (refresh succeeded) or false (refresh failed).
	 */
	private refreshCallbacks: Array<(success: boolean) => void> = [];

	/**
	 * Create a new API client instance
	 * @param baseUrl Optional base URL override
	 */
	constructor(baseUrl?: string) {
		this.baseUrl = baseUrl || apiPath.getBaseUrl();
	}

	/**
	 * Cancel all pending requests and clear the deduplication map.
	 * Intended to be called from SvelteKit's beforeNavigate to abort
	 * in-flight requests when the user navigates away from a page.
	 */
	cancelPending(): void {
		// Copy controllers then clear the set BEFORE aborting.
		// The catch handler in request() checks activeControllers.has(controller)
		// to distinguish navigation cancellation from timeout — clearing first
		// ensures the check works even if catch handlers run synchronously.
		const controllers = [...this.activeControllers];
		this.activeControllers.clear();
		this.inflightGets.clear();
		for (const controller of controllers) {
			controller.abort();
		}
	}

	/**
	 * Add authentication headers to request options
	 * @param options Request options
	 * @returns Updated request options with auth headers
	 */
	private addAuthHeaders(options: RequestInit = {}): ApiRequestOptions {
		// Skip adding auth headers during SSR
		if (!browser) {
			return {
				...options,
				credentials: 'include',
				headers: options.headers || {}
			};
		}

		// Get complete health store state
		const currentHealthState = getSnapshot();

		// Get API key from environment
		const apiKey = getApiKey();

		// Determine authentication strategy using the utility
		const authStrategy = determineAuthStrategy(currentHealthState, apiKey);

		// Get authentication headers based on the strategy
		const authHeaders = getAuthHeaders(authStrategy);

		// Update using_api_key_auth in secure storage if needed
		if (browser) {
			if (authStrategy.strategy === 'api_key') {
				secureStorage.setApiKeyAuth('true');
			} else {
				secureStorage.clearApiKeyAuth();
			}
		}

		return {
			...options,
			credentials: 'include',
			headers: {
				...options.headers,
				...authHeaders
			}
		};
	}

	/**
	 * Format a URL by prepending the base URL if needed
	 * @param endpoint API endpoint
	 * @returns Fully qualified URL
	 */
	private formatUrl(endpoint: string): string {
		return endpoint.startsWith('http')
			? endpoint
			: `${this.baseUrl}${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
	}

	/**
	 * Perform a generic HTTP request with error handling
	 * @param url Request URL
	 * @param options Request options
	 * @returns Promise with response data
	 */
	/** Default request timeout in milliseconds */
	private static readonly DEFAULT_TIMEOUT_MS = 30_000;

	async request<T>(url: string, options: RequestInit = {}): Promise<T> {
		const formattedUrl = this.formatUrl(url);
		const authOptions = this.addAuthHeaders(options);

		logger.debug('API request initiated', {
			url: formattedUrl,
			method: options.method || 'GET',
			hasBody: !!options.body
		});

		// Create an AbortController for timeout + navigation cancellation tracking.
		// If the caller provided their own signal, we still create our own controller
		// so cancelPending() works, and we listen on the caller's signal to propagate aborts.
		const controller = new AbortController();
		let timeoutId: ReturnType<typeof setTimeout> | null = null;
		let callerAbortHandler: (() => void) | null = null;

		if (authOptions.signal) {
			// Propagate caller's abort to our controller
			const callerSignal = authOptions.signal;
			if (callerSignal.aborted) {
				controller.abort();
			} else {
				callerAbortHandler = () => controller.abort();
				callerSignal.addEventListener('abort', callerAbortHandler, { once: true });
			}
		} else {
			// No caller signal — add default timeout
			timeoutId = setTimeout(() => controller.abort(), ApiClient.DEFAULT_TIMEOUT_MS);
		}

		authOptions.signal = controller.signal;
		this.activeControllers.add(controller);

		try {
			let response = await fetch(formattedUrl, authOptions);

			logger.debug('API response received', {
				url: response.url,
				status: response.status,
				ok: response.ok
			});

			// Handle 401 (unauthorized) — attempt token refresh then retry
			if (response.status === 401 && !url.includes('/auth/refresh')) {
				logger.info('Unauthorized response (401), attempting token refresh', { url });

				// If another request is already refreshing, queue this one
				if (this.isRefreshing) {
					logger.debug('Refresh already in progress, queuing request', { url });
					return new Promise<T>((resolve, reject) => {
						this.refreshCallbacks.push(async (success: boolean) => {
							if (success) {
								try {
									const retryResponse = await fetch(formattedUrl, this.addAuthHeaders(options));
									if (!retryResponse.ok) {
										reject(new Error(`Queued retry failed: ${retryResponse.status}`));
										return;
									}
									// Handle empty responses (204, etc.)
									const contentType = retryResponse.headers.get('content-type');
									if (retryResponse.status === 204 || !contentType?.includes('application/json')) {
										resolve({} as T);
										return;
									}
									const data = await retryResponse.json();
									resolve(data as T);
								} catch (err) {
									reject(err);
								}
							} else {
								reject(new Error('Token refresh failed'));
							}
						});
					});
				}

				this.isRefreshing = true;
				try {
					const refreshed = await tokenService.refreshToken();

					if (refreshed) {
						// Retry original request FIRST, before clearing refresh state
						logger.info('Token refreshed successfully, retrying request', { url });
						response = await fetch(formattedUrl, this.addAuthHeaders(options));

						// THEN clear and drain queued callbacks
						this.isRefreshing = false;
						const callbacks = [...this.refreshCallbacks];
						this.refreshCallbacks = [];
						callbacks.forEach(cb => cb(true));

						logger.debug('Request retried after token refresh', {
							url: response.url,
							status: response.status,
							ok: response.ok
						});

						if (response.status === 401) {
							// Second 401 after a fresh refresh — session is truly dead
							logger.warn('Second 401 after fresh token refresh, session is dead', { url });
							if (browser) {
								secureStorage.clearAll();
								const { clearAuthentication } = await import('$lib/state/auth.state.svelte');
								clearAuthentication();
								window.dispatchEvent(new CustomEvent('auth:session-expired'));
							}
							throw new Error('Session expired - token refresh succeeded but request still unauthorized');
						}
					} else {
						// Refresh returned false — clear refresh state and notify queued callbacks
						this.isRefreshing = false;
						const failCallbacks = [...this.refreshCallbacks];
						this.refreshCallbacks = [];
						failCallbacks.forEach(cb => cb(false));

						// Clear auth and show session expired overlay
						logger.warn('Token refresh returned false, clearing auth', { url });
						if (browser) {
							secureStorage.clearAll();
							const { clearAuthentication } = await import('$lib/state/auth.state.svelte');
							clearAuthentication();
							window.dispatchEvent(new CustomEvent('auth:session-expired'));
						}
						throw new Error('Token refresh failed - session expired');
					}
				} catch (refreshError) {
					this.isRefreshing = false;

					// Notify all queued callbacks of failure
					const callbacks = [...this.refreshCallbacks];
					this.refreshCallbacks = [];
					callbacks.forEach(cb => cb(false));

					// If this is our own thrown error from the blocks above, re-throw as-is
					if (refreshError instanceof Error && refreshError.message.includes('redirecting to login')) {
						throw refreshError;
					}

					// Distinguish network errors from auth errors
					const isNetworkError = refreshError instanceof TypeError ||
						(refreshError instanceof Error && (
							refreshError.message.includes('fetch') ||
							refreshError.message.includes('network') ||
							refreshError.message.includes('Failed to fetch') ||
							refreshError.message.includes('timed out') ||
							refreshError.message.includes('ERR_')
						));

					if (isNetworkError) {
						// Backend is down — don't kill the session, just let the request fail
						logger.warn('Token refresh failed due to network error — keeping session alive', {
							url,
							error: refreshError instanceof Error ? refreshError.message : String(refreshError)
						});
						throw new Error(`Network error during token refresh: ${refreshError instanceof Error ? refreshError.message : String(refreshError)}`);
					}

					// Actual auth error — clear session
					logger.error('Token refresh threw an auth error, clearing session', {
						url,
						error: refreshError instanceof Error ? refreshError.message : String(refreshError)
					});
					if (browser) {
						secureStorage.clearAll();
						const { clearAuthentication } = await import('$lib/state/auth.state.svelte');
						clearAuthentication();
						window.dispatchEvent(new CustomEvent('auth:session-expired'));
					}
					throw new Error('Token refresh failed - session expired');
				}
			}

			// Use ErrorUtils for consistent error handling
			return await ErrorUtils.handleApiResponse<T>(response);
		} catch (error) {
			// Handle AbortError: distinguish between timeout and navigation cancellation
			if (error instanceof DOMException && error.name === 'AbortError') {
				// cancelPending() removes the controller from activeControllers before aborting,
				// so if it's missing from the set this was a navigation cancellation — not a timeout.
				if (!this.activeControllers.has(controller)) {
					logger.debug('API request cancelled (navigation)', {
						url: formattedUrl,
						method: options.method || 'GET'
					});
					throw error;
				}
				// Controller still tracked means this was a timeout abort
				const effectiveTimeout = options.signal ? '(custom)' : `${ApiClient.DEFAULT_TIMEOUT_MS / 1000}s`;
				const timeoutError = new Error(`Request timed out after ${effectiveTimeout}: ${formattedUrl}`);
				logger.error('API request timed out', {
					url: formattedUrl,
					method: options.method || 'GET',
					timeoutMs: options.signal ? 'custom' : ApiClient.DEFAULT_TIMEOUT_MS
				});
				throw timeoutError;
			}
			logger.error('API request failed', {
				url: formattedUrl,
				method: options.method || 'GET',
				error: error instanceof Error ? error.message : String(error)
			});
			throw error;
		} finally {
			if (timeoutId) clearTimeout(timeoutId);
			this.activeControllers.delete(controller);
			if (callerAbortHandler && options.signal) {
				options.signal.removeEventListener('abort', callerAbortHandler);
			}
		}
	}

	/**
	 * Perform a GET request
	 * @param endpoint API endpoint
	 * @param params Optional query parameters (can be any object type)
	 * @returns Promise with response data
	 */
	async get<T, P extends object = Record<string, unknown>>(
		endpoint: string,
		params?: P
	): Promise<T> {
		const formattedUrl = this.formatUrl(endpoint);
		const url = new URL(formattedUrl, formattedUrl.startsWith('http') ? undefined : window.location.origin);

		// Add query parameters if provided
		if (params) {
			Object.keys(params).forEach((key) => {
				const value = params[key as keyof P];
				if (value !== undefined && value !== null) {
					// Convert all values to strings for URL parameters
					url.searchParams.append(key, String(value));
				}
			});
		}

		const fullUrl = url.toString();

		// Deduplicate: if this exact GET is already in-flight, reuse the existing promise
		const existing = this.inflightGets.get(fullUrl);
		if (existing) {
			logger.debug('GET request deduplicated (reusing in-flight)', { url: fullUrl });
			return existing as Promise<T>;
		}

		const promise = this.request<T>(fullUrl, { method: 'GET' })
			.finally(() => {
				this.inflightGets.delete(fullUrl);
			});

		this.inflightGets.set(fullUrl, promise);
		return promise;
	}

	/**
	 * Perform a POST request
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @param options Additional request options (e.g., signal for AbortController)
	 * @returns Promise with response data
	 */
	async post<T>(endpoint: string, data?: unknown, options?: RequestInit): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: data !== undefined ? JSON.stringify(data) : undefined,
			...options
		});
	}

	/**
	 * Perform a PUT request
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @returns Promise with response data
	 */
	async put<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'PUT',
			headers: {
				'Content-Type': 'application/json'
			},
			body: data !== undefined ? JSON.stringify(data) : undefined
		});
	}

	/**
	 * Perform a PATCH request
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @returns Promise with response data
	 */
	async patch<T>(endpoint: string, data?: unknown): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'PATCH',
			headers: {
				'Content-Type': 'application/json'
			},
			body: data !== undefined ? JSON.stringify(data) : undefined
		});
	}

	/**
	 * Perform a DELETE request
	 * @param endpoint API endpoint
	 * @returns Promise with response data
	 */
	async delete<T>(endpoint: string): Promise<T> {
		return this.request<T>(endpoint, {
			method: 'DELETE'
		});
	}

	/**
	 * Safe request that returns a tuple of [data, error]
	 * @param url Request URL
	 * @param options Request options
	 * @returns Promise with tuple of [data, error]
	 */
	async safeRequest<T>(url: string, options: RequestInit = {}): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.request<T>(url, options));
	}

	/**
	 * Safe GET request that returns a tuple of [data, error]
	 * @param endpoint API endpoint
	 * @param params Optional query parameters
	 * @returns Promise with tuple of [data, error]
	 */
	async safeGet<T, P extends object = Record<string, unknown>>(
		endpoint: string,
		params?: P
	): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.get<T, P>(endpoint, params));
	}

	/**
	 * Safe POST request that returns a tuple of [data, error]
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @returns Promise with tuple of [data, error]
	 */
	async safePost<T>(endpoint: string, data?: unknown): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.post<T>(endpoint, data));
	}

	/**
	 * Safe PUT request that returns a tuple of [data, error]
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @returns Promise with tuple of [data, error]
	 */
	async safePut<T>(endpoint: string, data?: unknown): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.put<T>(endpoint, data));
	}

	/**
	 * Safe PATCH request that returns a tuple of [data, error]
	 * @param endpoint API endpoint
	 * @param data Request body data
	 * @returns Promise with tuple of [data, error]
	 */
	async safePatch<T>(endpoint: string, data?: unknown): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.patch<T>(endpoint, data));
	}

	/**
	 * Safe DELETE request that returns a tuple of [data, error]
	 * @param endpoint API endpoint
	 * @returns Promise with tuple of [data, error]
	 */
	async safeDelete<T>(endpoint: string): Promise<[T | null, Error | null]> {
		return ErrorUtils.safeAwait(this.delete<T>(endpoint));
	}
}

// Create a singleton instance
export const apiClient = new ApiClient();

// Export the default instance
export default apiClient;
